<template>
  <SubPanel
    :label="$t('service.counter.panelLabel')"
    :countText="countText"
    icon="bi-person-slash"
    :accent="accent"
  >
    <p class="cu-intro">
      {{ $t('service.counter.intro') }}
    </p>

    <div class="cu-rows">
      <div v-for="c in counters" :key="c.zoneId" class="cu-row" :class="`is-${c.state}`">
        <div class="cu-zone">
          <span class="cu-dot" :style="{ background: accent }"></span>
          <span class="cu-name">{{ c.zone }}</span>
        </div>

        <template v-if="c.state === 'measured'">
          <div class="cu-fig" v-if="hasLimit(c)">
            <strong>{{ c.ownerThresholdWaits.count }}</strong>
            <span>{{ $t('service.counter.pastLimit') }}</span>
          </div>
          <div class="cu-fig" v-else>
            <strong>—</strong>
            <span>{{ $t('service.counter.noLimit') }}</span>
          </div>
          <div class="cu-fig">
            <strong>{{ dur(typ(c)) }}</strong>
            <span>{{ $t('service.counter.typically') }}</span>
          </div>
          <div class="cu-fig">
            <strong>{{ dur(lng(c)) }}</strong>
            <span>{{ $t('service.counter.longest') }}</span>
          </div>
          <div class="cu-fig">
            <!-- The Tooltip wrapper sits between .cu-fig and its children, which breaks
                 the flex column and lays the two out inline — where Vue's whitespace
                 condensing then removes the newline between them ("0.7%of counter
                 time"). Stack inside the tooltip instead of relying on the parent. -->
            <Tooltip :text="shareHelp(c)">
              <span class="cu-stack">
                <strong>{{ pct(c.unattendedSharePct) }}</strong>
                <span>{{ $t('service.counter.ofTime') }}</span>
              </span>
            </Tooltip>
          </div>
          <div class="cu-meta">
            <Tooltip :text="pairHelp(c)">{{ $t('service.counter.staffSide', { zone: c.staffZone }) }}</Tooltip>
          </div>
        </template>

        <div v-else class="cu-state">
          <i class="bi" :class="stateIcon(c)"></i>
          <span>{{ stateText(c) }}</span>
        </div>
      </div>
    </div>
  </SubPanel>
</template>

<script setup>
import { computed } from 'vue'
import SubPanel from '@/components/ui/SubPanel.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatDuration, formatPercent } from '@/i18n/format'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

const { t } = useI18n()
const isEn = () => currentLanguage() === 'en'

const props = defineProps({
  counters: { type: Array, default: () => [] },
  accent: { type: String, default: '#0D9488' },
})

function dur(s) {
  if (s == null) return '—'
  if (!isEn()) return formatDuration(s)
  const n = Math.round(s)
  if (n < 60) return `${n}s`
  const m = Math.floor(n / 60)
  const r = n % 60
  return r ? `${m}m ${r}s` : `${m}m`
}

// One decimal below 10%, whole numbers above — 0.7% and 31% are both readable, 0.71%
// and 31.4% are false precision on a figure nobody acts on to a tenth.
function pct(v) {
  if (v == null) return '—'
  if (!isEn()) return formatPercent(v < 10 ? v : Math.round(v), { decimals: v < 10 ? 1 : 0 })
  return v < 10 ? `${v.toFixed(1)}%` : `${Math.round(v)}%`
}

// Per-counter figures prefer the owner's-limit view (customers who found it unattended
// past the limit the shop set) over the raw cutoff count, which is mostly camera flicker.
// A counter with no limit set shows the neutral durations only, never a count.
// Interaction zone throughout — never "wait" (that is the queueing zone).
const hasLimit = (c) => !!(c.ownerThresholdWaits && c.ownerThresholdWaits.count != null)
const typ = (c) => hasLimit(c) ? c.ownerThresholdWaits.typicalSec : c.typicalWaitSec
const lng = (c) => hasLimit(c) ? c.ownerThresholdWaits.longestSec : c.longestWaitSec

const countText = computed(() => {
  const measured = props.counters.filter(c => c.state === 'measured')
  const counters = (n) => t('service.counter.countCounters', { n }, n)
  if (!measured.length) return counters(props.counters.length)
  const withLimit = measured.filter(hasLimit)
  if (!withLimit.length) return counters(measured.length)
  const total = withLimit.reduce((n, c) => n + (c.ownerThresholdWaits.count || 0), 0)
  return t('service.counter.countPastLimit', { total, counters: counters(measured.length) })
})

// Says what the number IS. An earlier version ended with "not worked out from the
// typical figures" — a defence against a misreading that the panel no longer invites,
// since it never shows two durations that could be subtracted. Explaining what we did
// not do reads as an apology.
const shareHelp = (c) => t('service.counter.shareHelp', { zone: c.zone })

const pairHelp = (c) => t('service.counter.pairHelp', { zone: c.staffZone })

function stateIcon(c) {
  if (c.state === 'needs_setup') return 'bi-plus-circle'
  if (c.state === 'pending') return 'bi-hourglass-split'
  return 'bi-slash-circle'
}

function stateText(c) {
  if (c.state === 'needs_setup') {
    const names = (c.counterpartTypes || []).map(ct => (isEn() ? ct.name : zoneTypeLabel(ct.name)))
    return names.length
      ? t('service.counter.addToMeasureCounter', { names: names.join(` ${t('service.counter.or')} `) })
      : t('service.counter.addStaffCounter')
  }
  if (c.state === 'pending') return t('service.counter.measuring')
  if (c.why === 'tripwire-only') return t('service.counter.tripwireOne')
  if (c.why === 'self_service') return t('service.counter.selfService')
  return t('service.counter.notMeasurable')
}
</script>

<style scoped>
.cu-intro {
  margin: 0 0 12px;
  font-size: 0.82rem;
  color: #55707f;
  line-height: 1.45;
  max-width: 68ch;
}
.cu-rows { display: flex; flex-direction: column; gap: 8px; }
.cu-row {
  display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e9ee;
  border-radius: 8px;
}
.cu-row.is-needs_setup, .cu-row.is-not_measurable { background: #f7f9fa; }
.cu-zone { display: flex; align-items: center; gap: 8px; min-width: 170px; }
.cu-dot { width: 10px; height: 10px; border-radius: 3px; flex: 0 0 auto; }
.cu-name { font-size: 0.86rem; color: #2b3a46; font-weight: 600; }
.cu-fig { display: flex; flex-direction: column; line-height: 1.2; }
.cu-stack { display: flex; flex-direction: column; line-height: 1.2; }
.cu-fig strong, .cu-stack strong { font-size: 1rem; color: #2b3a46; }
.cu-fig > span, .cu-stack > span { font-size: 0.7rem; color: #8aa2ae; }
.cu-meta { margin-left: auto; font-size: 0.72rem; color: #8aa2ae; }
.cu-state {
  display: flex; align-items: center; gap: 7px;
  font-size: 0.8rem; color: #55707f;
}
</style>
